import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  updateUserAttributes,
  getCurrentUser,
  fetchAuthSession,
} from "aws-amplify/auth";
import { uploadData, getUrl, remove } from "aws-amplify/storage";
import { Hub } from "aws-amplify/utils";
import { client } from "../../amplify-client";
import {
  PROFILE_IMAGE_OUTPUT_EXTENSION,
  PROFILE_IMAGE_OUTPUT_TYPE,
  clampImageOffsets,
  loadImageDimensionsFromUrl,
  renderCroppedProfileImage,
  revokeObjectUrl,
  validateProfileImageFile,
} from "../../components/profile-image-editor/profile-image-editor.functions";
import {
  evaluateClientTextModeration,
  extractModerationFeedback,
} from "../../api/moderation/content-moderation-service";
import {
  buildProfilePictureStoragePath,
  resolveProfilePictureOwnerId,
} from "../../api/profile/profile-picture-storage-path";

export const MINIMUM_PROFILE_AGE = 16;

export function calculateAge(dateOfBirth, today = new Date()) {
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
}

// Anti-circumvention for the self-declared age gate. When a DOB below the floor
// is entered we (a) don't reveal the threshold — a "you must be 16" message
// coaches re-entry of a fake older date — and (b) set a short cooling-off
// lockout so the same device can't immediately retry with a different date.
// Per ICO/FTC age-assurance guidance for self-declared gates.
export const UNDERAGE_LOCKOUT_STORAGE_KEY = "betterintentions.ageGateLockoutUntil";
export const UNDERAGE_LOCKOUT_MS = 24 * 60 * 60 * 1000;
export const UNDERAGE_GATE_MESSAGE =
  "Based on the information you provided, you're not able to create a BetterIntentions account right now.";

function defaultAgeGateStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
}

export function isUnderageDob(dateOfBirth, today = new Date()) {
  const age = calculateAge(dateOfBirth, today);
  return Number.isFinite(age) && age < MINIMUM_PROFILE_AGE;
}

export function recordUnderageLockout(storage = defaultAgeGateStorage(), now = new Date()) {
  if (!storage) return;
  try {
    storage.setItem(UNDERAGE_LOCKOUT_STORAGE_KEY, String(now.getTime() + UNDERAGE_LOCKOUT_MS));
  } catch {
    // Best-effort; a storage failure simply means no cooling-off this round.
  }
}

export function isUnderageLockoutActive(storage = defaultAgeGateStorage(), now = new Date()) {
  if (!storage) return false;
  const until = Number(storage.getItem(UNDERAGE_LOCKOUT_STORAGE_KEY));
  return Number.isFinite(until) && until > now.getTime();
}

export function validateCompleteProfileStep1(formData = {}) {
  if (!String(formData.profileName || "").trim() || !formData.state || !formData.city) {
    return "Please fill in all required fields";
  }

  return null;
}

export function validateCompleteProfileStep2(formData = {}, today = new Date()) {
  const hasRequiredFields =
    formData.dateOfBirth &&
    formData.ethnicity &&
    formData.maritalStatus &&
    formData.gender &&
    formData.sexualOrientation &&
    formData.religiousAffiliation &&
    formData.politicalAffiliation &&
    formData.story &&
    Array.isArray(formData.interests) &&
    formData.interests.length > 0;

  if (!hasRequiredFields) {
    return "Please fill in all required fields";
  }

  const age = calculateAge(formData.dateOfBirth, today);
  if (!Number.isFinite(age)) {
    return "Please fill in all required fields";
  }

  if (age < MINIMUM_PROFILE_AGE) {
    return `You must be at least ${MINIMUM_PROFILE_AGE} years old to use this platform`;
  }

  return null;
}

export function countAnsweredSociopoliticalQuestions(sociopoliticalFilters = {}) {
  return Object.values(sociopoliticalFilters).filter((question) => !question?.irrelevant).length;
}

export function validateCompleteProfileStep3(sociopoliticalFilters = {}, minRequiredAnswers = 8) {
  const answeredCount = countAnsweredSociopoliticalQuestions(sociopoliticalFilters);
  if (answeredCount < minRequiredAnswers) {
    return `Please answer at least 8 sociopolitical questions. You currently have ${answeredCount} answered.`;
  }

  return null;
}

export function validateCompleteProfileSubmitPreflight({ pendingImageFile, formData = {} } = {}) {
  if (pendingImageFile) {
    return "Please apply your profile photo adjustments before submitting.";
  }

  const sexualOrientation = String(formData.sexualOrientation || "").trim();
  if (!sexualOrientation) {
    return "Please select a sexual orientation";
  }

  return null;
}

export const COMPLETE_PROFILE_STEPS = [
  {
    id: 1,
    label: "Basic Info",
    title: "Basic Info",
    description: "",
  },
  {
    id: 2,
    label: "About You",
    title: "About You",
    description: "",
  },
  {
    id: 3,
    label: "Sociopolitical Views",
    title: "Sociopolitical Views",
    description: "",
  },
  {
    id: 4,
    label: "Profile Photo",
    title: "Add a profile photo (optional)",
    description: "",
  },
];

export const COMPLETE_PROFILE_ETHNICITY_OPTIONS = [
  { value: "American Indian or Alaska Native", label: "American Indian or Alaska Native" },
  { value: "Asian", label: "Asian" },
  { value: "Black or African American", label: "Black or African American" },
  { value: "Hispanic or Latino", label: "Hispanic or Latino" },
  { value: "Middle Eastern or North African", label: "Middle Eastern or North African" },
  { value: "Native Hawaiian or Other Pacific Islander", label: "Native Hawaiian or Other Pacific Islander" },
  { value: "White", label: "White" },
  { value: "Multiracial", label: "Multiracial" },
  { value: "Other", label: "Other" },
  { value: "Prefer not to say", label: "Prefer not to say" },
];

// Builds a unique storage path for a user's processed profile image upload.
export function buildCompleteProfilePicturePath(cognitoId, fileExtension, ownerId = cognitoId) {
  return buildProfilePictureStoragePath({
    ownerId,
    userId: cognitoId,
    fileExtension,
  });
}

// Detects duplicate-profile-name backend errors so the setup flow can show a friendlier message.
export function isCompleteProfileNameTakenError(message) {
  const normalizedMessage = String(message || "").toLowerCase();
  return (
    normalizedMessage.includes("profile name already taken") ||
    normalizedMessage.includes("e11000")
  );
}

// Converts the onboarding sociopolitical slider values into the persisted numeric answer shape.
export function normalizeCompleteProfileSociopoliticalAnswers(sociopoliticalFilters = {}) {
  return Object.entries(sociopoliticalFilters).reduce((accumulator, [key, question]) => {
    accumulator[key] = question?.irrelevant ? 0 : Number(question?.value ?? 4);
    return accumulator;
  }, {});
}

// Manages the CompleteProfile onboarding flow, including step state, validation, uploads, and submission.
export function useCompleteProfileForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [profilePictureUrl, setProfilePictureUrl] = useState("");
  const [persistedProfilePicturePath, setPersistedProfilePicturePath] = useState("");
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImageUrl, setPendingImageUrl] = useState("");
  const [pendingImageDimensions, setPendingImageDimensions] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffsetX, setImageOffsetX] = useState(0);
  const [imageOffsetY, setImageOffsetY] = useState(0);
  const [formData, setFormData] = useState({
    profileName: "",
    country: "United States",
    state: "",
    city: "",
    dateOfBirth: "",
    ethnicity: "",
    maritalStatus: "",
    occupation: "",
    religiousAffiliation: "",
    politicalAffiliation: "",
    gender: "",
    sexualOrientation: "",
    story: "",
    interests: [],
    profilePicture: "",
  });

  // Visibility for the sociopolitical answers entered below. Defaults to
  // public — cross-viewpoint discovery is the product; the user can hide the
  // answers here or later in Settings.
  const [sociopoliticalPublic, setSociopoliticalPublic] = useState(true);

  const [sociopoliticalFilters, setSociopoliticalFilters] = useState({
    politicalSpectrum: { value: null, irrelevant: false },
    immigration: { value: null, irrelevant: false },
    gunControl: { value: null, irrelevant: false },
    abortion: { value: null, irrelevant: false },
    climateChange: { value: null, irrelevant: false },
    taxPolicy: { value: null, irrelevant: false },
    lawEnforcement: { value: null, irrelevant: false },
    votingIntegrity: { value: null, irrelevant: false },
    regulatoryPhilosophy: { value: null, irrelevant: false },
    tradePolicy: { value: null, irrelevant: false },
    healthcare: { value: null, irrelevant: false },
  });

  const sociopoliticalQuestions = useMemo(
    () => [
      {
        id: "politicalSpectrum",
        label: "Political Spectrum",
        leftLabel: "Liberal",
        rightLabel: "Conservative",
      },
      {
        id: "immigration",
        label: "Immigration",
        leftLabel: "More Open Immigration",
        rightLabel: "Stricter Border Enforcement",
      },
      {
        id: "gunControl",
        label: "Gun Control",
        leftLabel: "Stricter Gun Laws",
        rightLabel: "Expanded Gun Rights",
      },
      {
        id: "abortion",
        label: "Abortion",
        leftLabel: "Broader Access",
        rightLabel: "More Restrictions",
      },
      {
        id: "climateChange",
        label: "Climate Change",
        leftLabel: "Urgent Climate Action",
        rightLabel: "Lower Climate Priority",
      },
      {
        id: "taxPolicy",
        label: "Tax Policy",
        leftLabel: "Higher Taxes & Services",
        rightLabel: "Lower Taxes & Smaller Government",
      },
      {
        id: "lawEnforcement",
        label: "Law Enforcement",
        leftLabel: "Reallocate Police Funding",
        rightLabel: "Expand Police Support",
      },
      {
        id: "votingIntegrity",
        label: "Voting Integrity",
        leftLabel: "Easier Ballot Access",
        rightLabel: "Stronger Voter ID Rules",
      },
      {
        id: "regulatoryPhilosophy",
        label: "Regulatory Philosophy",
        leftLabel: "More Federal Regulation",
        rightLabel: "Less Federal Regulation",
      },
      {
        id: "tradePolicy",
        label: "Trade Policy",
        leftLabel: "Free Trade Focus",
        rightLabel: "Tariff Protection Focus",
      },
      {
        id: "healthcare",
        label: "Healthcare",
        leftLabel: "Public Coverage Focus",
        rightLabel: "Private Coverage Focus",
      },
    ],
    []
  );

  const interestOptions = useMemo(
    () => [
      "Travel",
      "Cooking",
      "Sports",
      "Reading",
      "Music",
      "Art",
      "Technology",
      "Gaming",
      "Fitness",
      "Photography",
      "Writing",
      "Movies",
      "Fashion",
      "Volunteering",
      "Nature",
    ],
    []
  );

  const profileNameModeration = useMemo(
    () =>
      evaluateClientTextModeration({
        fieldType: "username",
        text: formData.profileName,
      }),
    [formData.profileName]
  );

  const storyModeration = useMemo(
    () =>
      evaluateClientTextModeration({
        fieldType: "profile_description",
        text: formData.story,
      }),
    [formData.story]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previousFormData) => ({
      ...previousFormData,
      [name]: value,
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      interests: previousFormData.interests.includes(interest)
        ? previousFormData.interests.filter((entry) => entry !== interest)
        : [...previousFormData.interests, interest],
    }));
  };

  const handleSociopoliticalChange = (questionId, value) => {
    setSociopoliticalFilters((previousFilters) => ({
      ...previousFilters,
      [questionId]: {
        ...previousFilters[questionId],
        value: parseInt(value, 10),
      },
    }));
  };

  const handleIrrelevantToggle = (questionId) => {
    setSociopoliticalFilters((previousFilters) => ({
      ...previousFilters,
      [questionId]: {
        ...previousFilters[questionId],
        irrelevant: !previousFilters[questionId].irrelevant,
        value: !previousFilters[questionId].irrelevant
          ? null
          : previousFilters[questionId].value,
      },
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateProfileImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    const nextPendingImageUrl = URL.createObjectURL(file);

    let dimensions = null;
    try {
      dimensions = await loadImageDimensionsFromUrl(nextPendingImageUrl);
    } catch {
      revokeObjectUrl(nextPendingImageUrl);
      setError("Failed to load image. Please try a different file.");
      return;
    }

    revokeObjectUrl(pendingImageUrl);
    setPendingImageFile(file);
    setPendingImageUrl(nextPendingImageUrl);
    setPendingImageDimensions(dimensions);
    setImageZoom(1);
    setImageOffsetX(0);
    setImageOffsetY(0);
    event.target.value = "";
  };

  const handleCancelImageAdjustments = () => {
    revokeObjectUrl(pendingImageUrl);
    setPendingImageFile(null);
    setPendingImageUrl("");
    setPendingImageDimensions(null);
    setImageZoom(1);
    setImageOffsetX(0);
    setImageOffsetY(0);
  };

  const handleApplyImageAdjustments = async () => {
    if (!pendingImageFile) return;

    setUploading(true);
    setError("");

    let uploadedFilePath = "";
    try {
      const user = await getCurrentUser();
      const cognitoId = user.userId;
      if (!cognitoId) throw new Error("User not authenticated");

      const previousFormPicturePath = formData.profilePicture;
      const clampedOffsets = clampImageOffsets(
        pendingImageDimensions,
        imageOffsetX,
        imageOffsetY,
        imageZoom
      );
      const processedBlob = await renderCroppedProfileImage({
        file: pendingImageFile,
        zoom: imageZoom,
        offsetX: clampedOffsets.x,
        offsetY: clampedOffsets.y,
      });
      const processedFile = new File([processedBlob], "profile-avatar.jpg", {
        type: PROFILE_IMAGE_OUTPUT_TYPE,
      });

      const profilePictureOwnerId = await resolveProfilePictureOwnerId({
        cognitoId,
        fetchAuthSessionImpl: fetchAuthSession,
      });
      const fileName = buildCompleteProfilePicturePath(
        cognitoId,
        PROFILE_IMAGE_OUTPUT_EXTENSION,
        profilePictureOwnerId
      );
      uploadedFilePath = fileName;
      await uploadData({
        path: fileName,
        data: processedFile,
        options: {
          contentType: PROFILE_IMAGE_OUTPUT_TYPE,
        },
      }).result;

      setFormData((previousFormData) => ({
        ...previousFormData,
        profilePicture: fileName,
      }));
      try {
        const urlResult = await getUrl({ path: fileName });
        setProfilePictureUrl(urlResult.url.toString());
      } catch (error) {
        console.error("Error fetching uploaded profile picture URL:", error);
        setProfilePictureUrl("");
      }

      if (
        previousFormPicturePath &&
        previousFormPicturePath !== fileName &&
        previousFormPicturePath !== persistedProfilePicturePath
      ) {
        try {
          await remove({ path: previousFormPicturePath });
        } catch (error) {
          console.error("Error deleting temporary profile picture:", error);
        }
      }

      revokeObjectUrl(pendingImageUrl);
      setPendingImageFile(null);
      setPendingImageUrl("");
      setPendingImageDimensions(null);
      setImageZoom(1);
      setImageOffsetX(0);
      setImageOffsetY(0);
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      setError("Failed to upload image. Please try again.");
      if (uploadedFilePath) {
        try {
          await remove({ path: uploadedFilePath });
        } catch (cleanupError) {
          console.error(
            "Error cleaning up failed profile image upload:",
            cleanupError
          );
        }
      }
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      revokeObjectUrl(pendingImageUrl);
    };
  }, [pendingImageUrl]);

  const handleNextStep = async (event) => {
    event.preventDefault();

    if (currentStep === 1) {
      const step1Error = validateCompleteProfileStep1(formData);
      if (step1Error) {
        setError(step1Error);
        return;
      }
      if (profileNameModeration.blocked) {
        setError(profileNameModeration.feedback);
        return;
      }

      setLoading(true);
      try {
        const user = await getCurrentUser();
        const cognitoId = user?.userId;
        if (!cognitoId) {
          throw new Error("User not authenticated");
        }

        const conflictResponse = await client.queries.checkProfileConflicts({
          cognitoId,
          profileName: formData.profileName.trim(),
        });

        if (conflictResponse?.errors?.length) {
          throw new Error(
            conflictResponse.errors.map((entry) => entry.message).join(", ")
          );
        }

        const conflictData =
          conflictResponse?.data?.checkProfileConflicts || conflictResponse?.data || {};

        if (conflictData?.profileNameTaken) {
          setError(
            "That username is already taken. Please choose a different profile name."
          );
          return;
        }
      } catch (error) {
        const moderationFeedback = extractModerationFeedback(error);
        if (moderationFeedback.isModerationError) {
          setError(moderationFeedback.feedback);
          return;
        }

        setError(
          error?.message ||
            "Unable to validate profile details right now. Please try again."
        );
        return;
      } finally {
        setLoading(false);
      }
    }

    if (currentStep === 2) {
      // Cooling-off: a recent underage attempt locks the gate without revealing
      // why, so the same device can't immediately retry with a faked DOB.
      if (isUnderageLockoutActive()) {
        setError(UNDERAGE_GATE_MESSAGE);
        return;
      }
      const step2Error = validateCompleteProfileStep2(formData);
      if (step2Error) {
        // Under the age floor: show a neutral message (don't reveal the
        // threshold) and start the cooling-off window. Other validation errors
        // (missing fields) surface normally.
        if (isUnderageDob(formData.dateOfBirth)) {
          recordUnderageLockout();
          setError(UNDERAGE_GATE_MESSAGE);
        } else {
          setError(step2Error);
        }
        return;
      }
      if (storyModeration.blocked) {
        setError(storyModeration.feedback);
        return;
      }
    }

    if (currentStep === 3) {
      const step3Error = validateCompleteProfileStep3(sociopoliticalFilters);
      if (step3Error) {
        setError(step3Error);
        return;
      }
    }

    setError("");
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setError("");
    setCurrentStep(currentStep - 1);
  };

  const handleStepSelect = (stepId) => {
    if (stepId > currentStep) return;
    setError("");
    setCurrentStep(stepId);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitValidationError = validateCompleteProfileSubmitPreflight({
        pendingImageFile,
        formData,
      });
      if (submitValidationError) {
        setError(submitValidationError);
        setLoading(false);
        return;
      }

      const sexualOrientation = (formData.sexualOrientation || "").trim();

      const user = await getCurrentUser();
      const cognitoId = user.userId;
      if (!cognitoId) {
        throw new Error("User not authenticated");
      }

      if (!formData.profileName.trim()) return;
      if (profileNameModeration.blocked) {
        throw new Error(profileNameModeration.feedback);
      }
      if (storyModeration.blocked) {
        throw new Error(storyModeration.feedback);
      }

      const response = await client.mutations.completeUserProfile({
        cognitoId,
        profile: {
          profileName: formData.profileName,
          location: {
            country: formData.country || "United States",
            state: formData.state,
            city: formData.city,
          },
          demographics: {
            dateOfBirth: formData.dateOfBirth
              ? new Date(formData.dateOfBirth).toISOString()
              : null,
            ethnicity: formData.ethnicity,
            maritalStatus: formData.maritalStatus,
            gender: formData.gender,
            sexualOrientation,
          },
          preferences: {
            occupation: formData.occupation,
            religiousAffiliation: formData.religiousAffiliation,
            politicalAffiliation: formData.politicalAffiliation,
          },
          story: formData.story,
          interests: formData.interests,
          profilePicture: formData.profilePicture,
          sociopoliticalAnswers:
            normalizeCompleteProfileSociopoliticalAnswers(sociopoliticalFilters),
          sociopoliticalPublic,
        },
      });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      const completedProfile =
        response?.data?.completeUserProfile || response?.data || null;
      if (!completedProfile?.isProfileComplete) {
        throw new Error("Profile could not be marked complete. Please try again.");
      }

      try {
        const userAttributesToUpdate = {
          name: formData.profileName.trim(),
        };
        await updateUserAttributes({
          userAttributes: userAttributesToUpdate,
        });
      } catch (attributeError) {
        console.error(
          "Failed to sync Cognito display name after profile save:",
          attributeError
        );
      }

      if (
        persistedProfilePicturePath &&
        formData.profilePicture &&
        persistedProfilePicturePath !== formData.profilePicture
      ) {
        try {
          await remove({ path: persistedProfilePicturePath });
        } catch (error) {
          console.error("Error deleting previous profile picture:", error);
        }
      }
      setPersistedProfilePicturePath(formData.profilePicture || "");

      Hub.dispatch("profile", {
        event: "profile-updated",
        data: { cognitoId },
      });

      navigate("/active", { replace: true });
    } catch (error) {
      const message = error?.message || "Something went wrong";
      const moderationFeedback = extractModerationFeedback(error);
      if (moderationFeedback.isModerationError) {
        setError(moderationFeedback.feedback);
        return;
      }

      if (isCompleteProfileNameTakenError(message)) {
        const friendlyMessage =
          "That username is already taken. Please choose a different profile name.";
        window.alert(friendlyMessage);
        setError(friendlyMessage);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    uploading,
    error,
    currentStep,
    formData,
    profilePictureUrl,
    hasPendingImage: Boolean(pendingImageFile),
    pendingImageUrl,
    pendingImageDimensions,
    imageZoom,
    imageOffsetX,
    imageOffsetY,
    profileNameModeration,
    storyModeration,
    sociopoliticalFilters,
    sociopoliticalQuestions,
    sociopoliticalPublic,
    setSociopoliticalPublic,
    interestOptions,
    handleChange,
    handleInterestToggle,
    handleSociopoliticalChange,
    handleIrrelevantToggle,
    handleImageUpload,
    setImageZoom,
    setImageOffsetX,
    setImageOffsetY,
    handleApplyImageAdjustments,
    handleCancelImageAdjustments,
    handleNextStep,
    handlePreviousStep,
    handleStepSelect,
    handleSubmit,
  };
}
